// Student Editor - PartCPage9 (Overview -  editable learner reflection and peer feedback)
import React from 'react';
import { useActivityContext } from '../index';
import useAutoSave from '../../../hooks/useAutoSave';

const PartCPage9 = () => {
  const { activity } = useActivityContext();

  const teacherFields = activity?.part_c?.page_9?.teacher_fields || {};

  const studentFields = useAutoSave(
    activity.activity_id,
    9,
    'student',
    activity.my_response_part_c?.page_9 || {},
    'part-c'
  );

  const handleTotalChange = (section, category, value) => {
    const currentSection = studentFields.data[section] || { awareness: 0, sensitivity: 0, creativity: 0 };
    const newSection = { ...currentSection, [category]: parseInt(value) || 0 };

    if (section.includes('learner_stage')) {
      const stage1 = section === 'learner_stage_1_total' ? newSection : (studentFields.data.learner_stage_1_total || { awareness: 0, sensitivity: 0, creativity: 0 });
      const stage2 = section === 'learner_stage_2_total' ? newSection : (studentFields.data.learner_stage_2_total || { awareness: 0, sensitivity: 0, creativity: 0 });

      studentFields.updateData({
        [section]: newSection,
        learner_overall_total: {
          awareness: stage1.awareness + stage2.awareness,
          sensitivity: stage1.sensitivity + stage2.sensitivity,
          creativity: stage1.creativity + stage2.creativity
        }
      });
    } else {
      studentFields.updateData({ [section]: newSection });
    }
  };

  const handlePerformanceChange = (section, category, value) => {
    const currentLevel = studentFields.data[section] || { awareness: '', sensitivity: '', creativity: '' };
    studentFields.updateData({
      [section]: { ...currentLevel, [category]: value }
    });
  };

  const renderSection = (title, bgColor, headerTextColor, stage1, stage2, stage3, performanceLevel, editable, showTotal = true, sectionKey) => {
    const learnerStage1 = studentFields.data.learner_stage_1_total || { awareness: 0, sensitivity: 0, creativity: 0 };
    const learnerStage2 = studentFields.data.learner_stage_2_total || { awareness: 0, sensitivity: 0, creativity: 0 };
    const learnerOverall = studentFields.data.learner_overall_total || { awareness: 0, sensitivity: 0, creativity: 0 };
    const peerStage3 = studentFields.data.peer_stage_3_total || { awareness: 0, sensitivity: 0, creativity: 0 };
    const learnerLevel = studentFields.data.learner_performance_level || { awareness: '', sensitivity: '', creativity: '' };
    const peerLevel = studentFields.data.peer_performance_level || { awareness: '', sensitivity: '', creativity: '' };

    const displayStage1 = sectionKey === 'learner' ? learnerStage1 : stage1;
    const displayStage2 = sectionKey === 'learner' ? learnerStage2 : stage2;
    const displayStage3 = sectionKey === 'peer' ? peerStage3 : stage3;
    const displayTotal = sectionKey === 'learner' ? learnerOverall : null;
    const displayLevel = sectionKey === 'learner' ? learnerLevel : sectionKey === 'peer' ? peerLevel : performanceLevel;

    const total = {
      awareness: (displayStage1?.awareness || 0) + (displayStage2?.awareness || 0) + (displayStage3?.awareness || 0),
      sensitivity: (displayStage1?.sensitivity || 0) + (displayStage2?.sensitivity || 0) + (displayStage3?.sensitivity || 0),
      creativity: (displayStage1?.creativity || 0) + (displayStage2?.creativity || 0) + (displayStage3?.creativity || 0)
    };

    return (
      <div style={{ marginBottom: '40px' }}>
        <div style={{
          backgroundColor: bgColor,
          color: headerTextColor,
          padding: '10px 20px',
          fontWeight: 'bold',
          fontSize: '18px',
          display: 'inline-block',
          marginBottom: '15px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {title}
        </div>

        {editable && sectionKey === 'learner' && (
          <div style={{ fontSize: '15px', marginBottom: '15px', lineHeight: '1.6', color: '#444', backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #F4824C' }}>
            1. For <strong>Stages 1 and 2</strong>, write the <strong>total number of</strong> statements the learner has ticked (✓).<br />
            2. Add the total here.
          </div>
        )}

        {editable && sectionKey === 'peer' && (
          <div style={{ fontSize: '15px', marginBottom: '15px', lineHeight: '1.6', color: '#444', backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #F4824C' }}>
            1. For <strong>Stage 3</strong>, write the <strong>total number of</strong> statements the peer has ticked (✓).<br />
            2. Tick the <strong>final performance level</strong> based on the score.
          </div>
        )}

        {/* Layout: Stacked - Table First, then Performance Levels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0',
              fontSize: '15px',
              backgroundColor: 'white',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #eee'
            }}>
              <thead>
                <tr style={{ backgroundColor: title.includes('Teacher') ? '#FFE0B2' : title.includes('Learner') ? '#FFF176' : '#FFECB3' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid rgba(0,0,0,0.1)', minWidth: '150px' }}>Stage</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid rgba(0,0,0,0.1)', minWidth: '120px' }}>Awareness</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid rgba(0,0,0,0.1)', minWidth: '120px' }}>Sensitivity</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid rgba(0,0,0,0.1)', minWidth: '120px' }}>Creativity</th>
                </tr>
              </thead>
              <tbody>
                {stage1 !== null && (
                  <tr>
                    <td style={{ padding: '12px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>Stage 1</td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'learner' ? (
                        <input
                          type="number"
                          min="0"
                          value={learnerStage1.awareness}
                          onChange={(e) => handleTotalChange('learner_stage_1_total', 'awareness', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage1?.awareness || 0}</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'learner' ? (
                        <input
                          type="number"
                          min="0"
                          value={learnerStage1.sensitivity}
                          onChange={(e) => handleTotalChange('learner_stage_1_total', 'sensitivity', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage1?.sensitivity || 0}</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'learner' ? (
                        <input
                          type="number"
                          min="0"
                          value={learnerStage1.creativity}
                          onChange={(e) => handleTotalChange('learner_stage_1_total', 'creativity', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage1?.creativity || 0}</span>}
                    </td>
                  </tr>
                )}
                {stage2 !== null && (
                  <tr>
                    <td style={{ padding: '12px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>Stage 2</td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'learner' ? (
                        <input
                          type="number"
                          min="0"
                          value={learnerStage2.awareness}
                          onChange={(e) => handleTotalChange('learner_stage_2_total', 'awareness', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage2?.awareness || 0}</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'learner' ? (
                        <input
                          type="number"
                          min="0"
                          value={learnerStage2.sensitivity}
                          onChange={(e) => handleTotalChange('learner_stage_2_total', 'sensitivity', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage2?.sensitivity || 0}</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'learner' ? (
                        <input
                          type="number"
                          min="0"
                          value={learnerStage2.creativity}
                          onChange={(e) => handleTotalChange('learner_stage_2_total', 'creativity', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage2?.creativity || 0}</span>}
                    </td>
                  </tr>
                )}
                {stage3 !== null && (
                  <tr>
                    <td style={{ padding: '12px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>Stage 3</td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'peer' ? (
                        <input
                          type="number"
                          min="0"
                          value={peerStage3.awareness}
                          onChange={(e) => handleTotalChange('peer_stage_3_total', 'awareness', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage3?.awareness || 0}</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'peer' ? (
                        <input
                          type="number"
                          min="0"
                          value={peerStage3.sensitivity}
                          onChange={(e) => handleTotalChange('peer_stage_3_total', 'sensitivity', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage3?.sensitivity || 0}</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      {editable && sectionKey === 'peer' ? (
                        <input
                          type="number"
                          min="0"
                          value={peerStage3.creativity}
                          onChange={(e) => handleTotalChange('peer_stage_3_total', 'creativity', e.target.value)}
                          style={{ width: '80px', padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px', fontSize: '15px' }}
                        />
                      ) : <span style={{ fontWeight: '500' }}>{displayStage3?.creativity || 0}</span>}
                    </td>
                  </tr>
                )}
                {showTotal && (
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td style={{ padding: '12px', fontWeight: 'bold', color: '#333' }}>Total</td>
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#F4824C', fontSize: '16px' }}>
                      {sectionKey === 'learner' ? learnerOverall.awareness : total.awareness}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#F4824C', fontSize: '16px' }}>
                      {sectionKey === 'learner' ? learnerOverall.sensitivity : total.sensitivity}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#F4824C', fontSize: '16px' }}>
                      {sectionKey === 'learner' ? learnerOverall.creativity : total.creativity}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Performance Level Section */}
          <div style={{
            backgroundColor: '#fcfcfc',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #eee',
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
          }}>
            {editable && (sectionKey === 'learner' || sectionKey === 'peer') && (
              <div style={{ marginBottom: '20px', fontWeight: 'bold', fontSize: '16px', color: '#333' }}>
                3. Tick the <strong>final performance level</strong> based on the final score.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {['Awareness', 'Sensitivity', 'Creativity'].map((category, idx) => {
                const levels = sectionKey === 'peer' ?
                  ['Beginner\n(0-1)', 'Proficient\n(2)', 'Advanced\n(3)'] :
                  ['Beginner\n(0-5)', 'Proficient\n(6-10)', 'Advanced\n(11-16)'];

                return (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    paddingBottom: idx < 2 ? '20px' : '0',
                    borderBottom: idx < 2 ? '1px dashed #eee' : 'none'
                  }}>
                    {/* Category Label */}
                    <div style={{
                      backgroundColor: '#3F51B5',
                      color: 'white',
                      padding: '10px 15px',
                      fontWeight: 'bold',
                      width: '120px',
                      textAlign: 'center',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {category}
                    </div>

                    {/* Levels */}
                    <div style={{ display: 'flex', gap: '30px', flex: 1, justifyContent: 'space-around' }}>
                      {levels.map((level, levelIdx) => {
                        const levelValue = level.split('\n')[0];
                        const isChecked = displayLevel && displayLevel[category.toLowerCase()] === levelValue;

                        return (
                          <div key={levelIdx} style={{
                            position: 'relative',
                            cursor: editable ? 'pointer' : 'default',
                            transition: 'transform 0.2s'
                          }}
                            onClick={() => editable && handlePerformanceChange(
                              sectionKey === 'learner' ? 'learner_performance_level' : 'peer_performance_level',
                              category.toLowerCase(),
                              levelValue
                            )}
                          >
                            {/* Circle */}
                            <div style={{
                              backgroundColor: isChecked ? '#FFF176' : '#fff',
                              border: isChecked ? '3px solid #F4824C' : '2px solid #ddd',
                              borderRadius: '50%',
                              width: '80px',
                              height: '80px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              boxShadow: isChecked ? '0 4px 12px rgba(244, 130, 76, 0.3)' : '0 2px 6px rgba(0,0,0,0.05)'
                            }}>
                              {level.split('\n').map((line, i) => (
                                <div key={i} style={{
                                  fontSize: i === 0 ? '13px' : '15px',
                                  fontWeight: i === 0 ? 'normal' : 'bold',
                                  color: '#333',
                                  lineHeight: '1.2'
                                }}>
                                  {line}
                                </div>
                              ))}
                            </div>

                            {/* Checkmark Badge */}
                            {isChecked && (
                              <div style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#4CAF50',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '2px solid white'
                              }}>
                                ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '1400px',
      minHeight: '80vh',
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      borderRadius: '8px',
      boxSizing: 'border-box',
      position: 'relative',
      fontFamily: "'Poppins', Arial, sans-serif"
    }}>
      {/* Save indicator */}
      {studentFields.saveStatus === 'saving' && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#FFC107',
          color: 'black',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          Saving...
        </div>
      )}
      {studentFields.saveStatus === 'saved' && studentFields.lastSaved && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#4CAF50',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          Saved ✓
        </div>
      )}

      {/* Header */}
      <div style={{
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '10px',
        textAlign: 'center',
        backgroundColor: '#F4824C',
        color: 'white',
        padding: '15px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(244, 130, 76, 0.2)'
      }}>
        Overview
      </div>
      <div style={{
        fontSize: '16px',
        marginBottom: '40px',
        textAlign: 'center',
        color: '#666',
        padding: '10px',
        backgroundColor: '#fff5ec',
        borderRadius: '6px',
        border: '1px solid #ffe0cc'
      }}>
        Tabulate all the feedback for the research task on this page.
      </div>

      {/* Teacher Assessment - Read Only */}
      {renderSection(
        'Teacher Assessment',
        '#F4824C',
        'white',
        teacherFields.stage_1_total,
        teacherFields.stage_2_total,
        teacherFields.stage_3_total,
        teacherFields.performance_level,
        false,
        true,
        'teacher'
      )}

      {/* Learner Reflection - Editable */}
      {renderSection(
        'Learner Reflection',
        '#FFD966',
        'black',
        null,
        null,
        null,
        null,
        true,
        true,
        'learner'
      )}

      {/* Peer Feedback - Editable */}
      {renderSection(
        'Peer Feedback',
        '#FFD966',
        'black',
        null,
        null,
        {},
        null,
        true,
        false,
        'peer'
      )}

      {/* Page Number */}
      <div style={{
        textAlign: 'center',
        marginTop: '60px',
        fontSize: '16px',
        color: '#999',
        fontWeight: 'bold'
      }}>
        27
      </div>
    </div>
  );
};

export default PartCPage9;
